import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ClassNameMap } from '@mui/styles';
import { Trans } from '@lingui/react';
import { FormikHelpers } from 'formik';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import { BasicDisplayUnits, getPressurePumpUnitLabel, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { Form } from 'hooks/useForm';
import {
  BackfillRecipeForm,
  CustomUnitFields,
  MetricPressureUnit,
  PumpType,
  ThroughputControlType,
  UcsCoefficientsDto,
  UnitSystem
} from 'providers/api';
import { sanitiseNumber } from 'utils/form';
import {
  gravityMetricHeadToPumpPressureBar,
  pumpPressureBarToGravityMetricHead,
  pumpPressureBarToKpa,
  pumpPressureKpaToBar
} from 'utils/unitConversions';
import { DictionaryRange, OptimisationErrorType } from '../../../MineModel/types';
import UnitSystemNumberField from './UnitSystemNumberField';
import UnitSystemNumberSlider from './UnitSystemNumberSlider';

interface PasteTargetInputsProps {
  isOptimised: boolean;
  values: BackfillRecipeForm;
  limits: DictionaryRange;
  maxPumpPressure: number | undefined;
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto } | undefined
  hasUcsData: boolean;
  optimisationError: OptimisationErrorType;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  throughputControlType: ThroughputControlType;
  classes: ClassNameMap;
  pumpType: PumpType;
  metricPressureUnit?: MetricPressureUnit;
  handleBlur: (e: any) => void | void;
  setFieldValue: FormikHelpers<BackfillRecipeForm>['setFieldValue'];
  hasError: Form['helpers']['hasError'];
  getErrorHelpText: Form['helpers']['getErrorHelpText'];
  setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void;
}

const getThroughputLabel = (throughputControlType: ThroughputControlType) => {
  switch (throughputControlType) {
    case ThroughputControlType.WetTonnage:
      return <Trans id="Wet Tonnage">Wet Tonnage</Trans>;
    case ThroughputControlType.FlowRate:
      return <Trans id="Flow Rate">Flow Rate</Trans>;
    case ThroughputControlType.DryTonnage:
      return <Trans id="Dry Tonnage">Dry Tonnage</Trans>;
    default:
      return null;
  }
};

const convertTextByPumpType = (pumpType: PumpType) => (
  pumpType === PumpType.Pump
    ? <Trans id="Pump Pressure">Pump Pressure</Trans>
    : <Trans id="Gravity">Gravity</Trans>
);

const getPumpOrGravityHelperText = (
  pumpType: PumpType,
  hasError: (field: string) => boolean,
  getErrorHelpText: (field: string) => string,
): React.ReactNode => {
  const errorKey = pumpType === PumpType.Pump
    ? 'pasteSpecification.pumpPressure'
    : 'pasteSpecification.gravity';

  return hasError(errorKey)
    ? <Trans id={getErrorHelpText(errorKey)}>{getErrorHelpText(errorKey)}</Trans>
    : '';
};

const getPumpOrGravityStep = (pumpType: PumpType, maxDisplayValue: number) => {
  if (pumpType === PumpType.Pump) return 1;
  return maxDisplayValue <= 10 ? 0.1 : 1;
};

const PasteTargetInputs = ({
  isOptimised,
  values,
  limits,
  maxPumpPressure,
  targetDaysCoefficients,
  hasUcsData,
  optimisationError,
  unitSystemPreference,
  customUnitFields,
  throughputControlType,
  classes,
  pumpType,
  metricPressureUnit,
  handleBlur,
  setFieldValue,
  hasError,
  getErrorHelpText,
  setFieldTouched,
}: PasteTargetInputsProps) => {
  const isKiloPascal = pumpType === PumpType.Pump
    && metricPressureUnit === MetricPressureUnit.KiloPascal
    && unitSystemPreference === UnitSystem.Metric;
  const pumpPressureBar = values.pasteSpecification?.pumpPressure ?? limits.pumpPressure.default ?? 0;
  const maxPumpPressureBar = maxPumpPressure ?? limits.pumpPressure.max;
  const toDisplayPump = (bar: number) => (isKiloPascal ? pumpPressureBarToKpa(bar) : bar);
  const fromDisplayPump = (displayValue: number) => (isKiloPascal ? pumpPressureKpaToBar(displayValue) : displayValue);
  const displayPumpOrGravity = pumpType === PumpType.Pump
    ? toDisplayPump(pumpPressureBar)
    : pumpPressureBarToGravityMetricHead(pumpPressureBar);
  const displayMaxPumpOrGravity = pumpType === PumpType.Pump
    ? toDisplayPump(maxPumpPressureBar)
    : pumpPressureBarToGravityMetricHead(maxPumpPressureBar);
  const displayStep = pumpType === PumpType.Pump && isKiloPascal ? 100 : getPumpOrGravityStep(pumpType, displayMaxPumpOrGravity);
  const pumpPressureUnitInfo = pumpType === PumpType.Pump
    ? getPressurePumpUnitLabel(metricPressureUnit ?? MetricPressureUnit.Bar)
    : unitLabels[BasicDisplayUnits.Gravity];

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Typography variant="caption"><Trans id="Paste Fill">Paste Fill</Trans></Typography>
      </Grid>
      <Grid size={12}>
        <Box>
          <FormHelperText id="recipe-reference-helper-text" component="label"><Trans id="Recipe Reference">Recipe Reference</Trans></FormHelperText>
          <TextField
            size="small"
            error={hasError('reference')}
            helperText={hasError('reference') ? <Trans id={getErrorHelpText('reference')}>{getErrorHelpText('reference')}</Trans> : ''}
            required
            autoFocus
            fullWidth
            id="reference"
            name="reference"
            aria-labelledby="recipe-reference-helper-text"
            value={values.reference}
            disabled={isOptimised}
            onChange={(e) => setFieldValue('reference', e.target.value)}
            onBlur={handleBlur}
          />
        </Box>
      </Grid>

      {/* Required fields */}

      <Grid size={5}>
        <FormHelperText id="pump-pressure-helper-text" component="label">{convertTextByPumpType(pumpType)}</FormHelperText>
        <UnitSystemNumberField
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={pumpPressureUnitInfo}
          value={displayPumpOrGravity}
          onChange={(event) => {
            const inputValue = sanitiseNumber(event.target.value) ?? 0;
            const valueInBar = pumpType === PumpType.Pump ? fromDisplayPump(inputValue) : gravityMetricHeadToPumpPressureBar(inputValue);
            setFieldValue('pasteSpecification.pumpPressure', valueInBar);
          }}
          decimalPlaces={pumpType === PumpType.Pump ? 0 : 1}
          size="small"
          id={pumpType === PumpType.Pump ? 'pump-pressure' : 'gravity'}
          name={pumpType === PumpType.Pump ? 'pasteSpecification.pumpPressure' : 'pasteSpecification.gravity'}
          error={hasError(
            pumpType === PumpType.Pump
              ? 'pasteSpecification.pumpPressure'
              : 'pasteSpecification.gravity',
          )}
          helperText={getPumpOrGravityHelperText(pumpType, hasError, getErrorHelpText)}
          aria-labelledby="pump-pressure-helper-text"
          onBlur={handleBlur}
          disabled={isOptimised}
          showZero
        />

      </Grid>
      <Grid size={7}>
        <Box
          sx={{
            ml: 1,
            mr: 2,
            pt: 2,
          }}
        >
          <UnitSystemNumberSlider
            unitSystem={unitSystemPreference}
            customUnitFields={customUnitFields}
            unitInfo={pumpPressureUnitInfo}
            value={displayPumpOrGravity}
            decimalPlaces={5}
            step={displayStep}
            min={0}
            max={displayMaxPumpOrGravity}
            name="pasteSpecification.pumpPressure"
            onBlur={handleBlur}
            onSliderChange={(event, newValue) => {
              const sliderValue = newValue as number;
              const valueInBar = pumpType === PumpType.Pump ? fromDisplayPump(sliderValue) : gravityMetricHeadToPumpPressureBar(sliderValue);
              setFieldValue('pasteSpecification.pumpPressure', valueInBar);
            }}
            valueLabelDisplay="auto"
            disabled={isOptimised}
            aria-labelledby="pump-pressure-helper-text"
          />
        </Box>
      </Grid>

      <Grid size={5}>
        <FormHelperText id="tonnage-helper-text" component="label">
          {getThroughputLabel(throughputControlType)}
        </FormHelperText>
        <UnitSystemNumberField
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
          disabled={throughputControlType !== ThroughputControlType.FlowRate}
          onChange={(event) => setFieldValue('pasteSpecification.throughput', event.target.value)}
          decimalPlaces={0}
          id="tonnage-input"
          size="small"
          name="tonnage-input"
          aria-labelledby="tonnage-helper-text"
          value={values.pasteSpecification?.throughput ?? limits.tons.default ?? limits.tons.min}
          error={hasError('pasteSpecification.throughput')}
          helperText={
            hasError('pasteSpecification.throughput')
              ? <Trans id={getErrorHelpText('pasteSpecification.throughput')}>{getErrorHelpText('pasteSpecification.throughput')}</Trans>
              : ''
          }
          onBlur={handleBlur}
          InputProps={{
            endAdornment: <InputAdornment position="end">t/h</InputAdornment>,
          }}
        />

      </Grid>
      <Grid size={7}>
        <Box
          sx={{
            ml: 1,
            mr: 2,
            pt: 2,
          }}
        >
          <UnitSystemNumberSlider
            unitSystem={unitSystemPreference}
            customUnitFields={customUnitFields}
            unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
            value={values.pasteSpecification?.throughput ?? limits.tons.default ?? limits.tons.min}
            decimalPlaces={0}
            min={limits.tons.min}
            baseStep={limits.tons.interval}
            max={limits.tons.max}
            onSliderChange={(event: any, newValue: number | number[]) => setFieldValue('pasteSpecification.throughput', newValue as number)}
            valueLabelDisplay="auto"
            disabled={isOptimised}
            aria-labelledby="tonnage-helper-text"
          />
        </Box>
      </Grid>

      {hasUcsData && (
        <>
          <Grid size={7}>
            <FormHelperText id="target-ucs-strength-helper-text" component="label"><Trans id="Target UCS">Target UCS</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
              value={values.pasteSpecification?.targetUCSStrength}
              onChange={(event) => {
                setFieldValue('pasteSpecification.targetUCSStrength', sanitiseNumber(event.target.value));
                // Touching must not validate: Formik would validate the pre-change value, and that stale result lands last.
                setFieldTouched('pasteSpecification.targetUCSStrength', true, false);
              }}
              decimalPlaces={0}
              error={hasError('pasteSpecification.targetUCSStrength')}
              helperText={
                hasError('pasteSpecification.targetUCSStrength')
                  ? <Trans id={getErrorHelpText('pasteSpecification.targetUCSStrength')}>{getErrorHelpText('pasteSpecification.targetUCSStrength')}</Trans>
                  : ''
              }
              id="pasteSpecification.targetUCSStrength"
              name="pasteSpecification.targetUCSStrength"
              aria-labelledby="target-ucs-strength-helper-text"
              disabled={isOptimised}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid size={1}>
            <div className={classes.at}>
              <Trans id="at">at</Trans>
            </div>
          </Grid>
          <Grid size={4}>
            <FormHelperText id="target-days-helper-text" component="label"><Trans id="Target Days">Target Days</Trans></FormHelperText>
            <TextField
              size="small"
              type="number"
              error={hasError('pasteSpecification.targetDays')}
              helperText={
                hasError('pasteSpecification.targetDays')
                  ? <Trans id={getErrorHelpText('pasteSpecification.targetDays')}>{getErrorHelpText('pasteSpecification.targetDays')}</Trans>
                  : ''
              }
              fullWidth
              select
              id="pasteSpecification.targetDays"
              name="pasteSpecification.targetDays"
              aria-labelledby="target-days-helper-text"
              value={values.pasteSpecification?.targetDays ?? Object.keys(targetDaysCoefficients ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b))[0]}
              disabled={isOptimised}
              onChange={(e) => setFieldValue('pasteSpecification.targetDays', parseFloat(e.target.value))}
              onBlur={handleBlur}
            >
              {Object.keys(targetDaysCoefficients ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b)).map(
                (numberOfDays) => <MenuItem key={`key-${numberOfDays}`} value={numberOfDays as string}>{numberOfDays}</MenuItem>,
              )}
            </TextField>
          </Grid>
        </>
      )}

      {optimisationError !== OptimisationErrorType.NONE && (
        <Grid size={12}>
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', pb: 1 }}>
            <Trans id="Optimisation error. No valid recipe found for the given parameters.">
              Optimisation error. No valid recipe found for the given parameters.
            </Trans>
          </Typography>
        </Grid>
      )}

    </Grid>
  );
};

export default PasteTargetInputs;
